import { Dropdown } from '@/components/Select/Dropdown'
import { TextArea } from '@/components/TextArea/TextArea'
import { useCopy } from '@/hooks/useCopy'
import CheckSvg from '@/icons/check.svg?react'
import ChevronDownSvg from '@/icons/chevron-down.svg?react'
import CopySvg from '@/icons/copy.svg?react'
import { RemovePostPopup } from '@/pages/PostsPage/RemovePostPopup/RemovePostPopup'
import { usePostsState } from '@/state/posts'
import { useProjectsState } from '@/state/projects'
import classNames from 'classnames'
import { formatDistanceToNowStrict } from 'date-fns'
import debounce from 'lodash.debounce'
import type { FunctionalComponent } from 'preact'
import { useCallback, useMemo, useState } from 'preact/hooks'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { useShallow } from 'zustand/shallow'
import styles from './index.module.scss'

export const PostItem: FunctionalComponent<{
  postId: number
}> = props => {
  const copy = useCopy()
  const [showRemove, setShowRemove] = useState(false)

  const post = usePostsState(useShallow(
    s => s.list.find(post => post.id === props.postId),
  ))

  const patchPost = usePostsState(s => s.patch)
  const setContent = usePostsState(s => s.setContent)
  const setProject = usePostsState(s => s.setProject)

  const projectsList = useProjectsState(s => s.list)

  const project = useProjectsState(useShallow(
    s => s.list.find(project => project.id === post?.projectId),
  ))

  const dateStr = useMemo(() => {
    return post ? formatDistanceToNowStrict(new Date(post.createdAt)) : null
  }, [post])

  const xLink = useMemo(() => {
    if (!post?.content) return null
    const encoded = encodeURIComponent(post.content)
    return `https://twitter.com/intent/tweet?text=${encoded}`
  }, [post?.content])

  const saveDebounced = useCallback(
    debounce((content: string) => {
      patchPost(props.postId, { content })
    }, 500),
    [props.postId],
  )

  if (!post || !project) return null

  return (
    <>
      {showRemove && (
        <RemovePostPopup
          postId={props.postId}
          onClose={() => {
            setShowRemove(false)
          }}
        />
      )}

      <div class={styles.root}>
        <div class={styles.header}>
          <div class={styles.main}>
            <Dropdown
              width={200}
              placement='bottom-start'
              value={project.id}
              options={projectsList.map(project => ({
                value: project.id,
                label: project.title,
              }))}
              onChange={projectId => {
                try {
                  patchPost(props.postId, { projectId })
                  setProject(props.postId, projectId)
                }
                catch (e) {
                  console.error(e)
                }
              }}
            >
              {({ triggerProps, setRef: ref, isOpen }) => (
                <button
                  {...triggerProps()}
                  ref={ref}
                  class={classNames(styles.title, {
                    [styles.active]: isOpen,
                  })}
                >
                  {project.title}
                  <ChevronDownSvg
                    class={styles.toggler}
                  />
                </button>
              )}
            </Dropdown>

            <div class={styles.date}>
              {dateStr}
            </div>
          </div>

          <div class={styles.tools}>
            <button
              class={styles.tool}
              onClick={() => {
                setShowRemove(true)
              }}
            >
              remove
            </button>

            <CopyToClipboard
              text={post.content}
              onCopy={copy.setSuccess}
            >
              <button class={classNames(styles.tool, styles.copy)}>
                {copy.copied ? <CheckSvg /> : <CopySvg />}
                copy
              </button>
            </CopyToClipboard>

            {xLink && (
              <a
                href={xLink}
                target='_blank'
                class={styles.tool}
              >
                𝕏
              </a>
            )}
          </div>
        </div>

        <TextArea
          view='post'
          value={post.content}
          onChange={value => {
            setContent(props.postId, value)
            saveDebounced(value)
          }}
        />
      </div>
    </>
  )
}
